import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useAuth } from '../App.jsx';

/**
 * Мейл към длъжници — текстът на напомнянето.
 *
 * Писмото е от фирмата до клиент и думите в него са на собственика, не
 * на програмата. Затова се редактира оттук, а не се сменя в кода.
 *
 * Полетата не са едно голямо поле нарочно. Писмото има части, които
 * програмата подрежда сама — таблицата с фактурите, сумите, подписът —
 * и ако целият текст беше свободен, всяка промяна щеше да рискува да
 * изтрие мястото, където те влизат.
 *
 * Две от полетата идват по двойки, защото са една и съща мисъл в два
 * случая: когато всичко изброено е с настъпил падеж и когато сред него
 * има и непадежирало. Писмо, което твърди, че всичко е падежирало,
 * докато две фактури са отпреди два дни, се хваща от всеки, който си
 * следи сметките.
 */

const POLETA = [
  {
    key: 'subject',
    label: 'Заглавие',
    hint: 'Появява се в списъка с писма при получателя.',
  },
  {
    key: 'greeting',
    label: 'Обръщение',
  },
  {
    key: 'intro_all',
    label: 'Увод — когато всичко е с настъпил падеж',
    rows: 3,
  },
  {
    key: 'intro_mixed',
    label: 'Увод — когато има и непадежирали',
    rows: 3,
    hint: 'Ползва се само ако сред неплатените има фактури, чийто падеж още не е дошъл.',
  },
  {
    key: 'oldest',
    label: 'За най-старото задължение',
    rows: 2,
  },
  {
    key: 'before_table',
    label: 'Преди таблицата',
    rows: 2,
  },
  {
    key: 'upcoming_note',
    label: 'Бележка над непадежиралите',
    rows: 2,
    hint: 'Стои в таблицата, точно преди фактурите, за които още не се иска плащане.',
  },
  {
    key: 'request_all',
    label: 'Искане — когато всичко е с настъпил падеж',
    rows: 2,
  },
  {
    key: 'request_mixed',
    label: 'Искане — когато има и непадежирали',
    rows: 2,
  },
  {
    key: 'closing',
    label: 'Накрая',
    rows: 8,
    hint: 'Празен ред дели абзаците.',
  },
  {
    key: 'signoff',
    label: 'Поздрав преди подписа',
    hint: 'Под него програмата слага името на фирмата, лицето за контакт, телефона и адреса.',
  },
];

export default function DebtLetter() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get('/api/mail/debt-letter')
      .then((d) => { setData(d); setForm(d.template); })
      .catch((err) => setError(err.message));
  }, []);

  // Т. 19: одиторът вижда текста на писмата, но не го сменя.
  const canWrite = user.role === 'admin';
  if (error && !data) return <div className="error">{error}</div>;
  if (!data) return <div className="note">…</div>;

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDone(null);
  };

  // Промяна спрямо това, което е записано — дотогава няма какво да се пише.
  const changed = POLETA.some((p) => (form[p.key] ?? '') !== (data.template[p.key] ?? ''));
  const custom = (key) => (form[key] ?? '') !== data.defaults[key];

  async function save() {
    setBusy(true); setError(null); setDone(null);
    try {
      const res = await api.put('/api/mail/debt-letter', form);
      setData(res);
      setForm(res.template);
      setDone('Текстът е записан. Следващото напомняне тръгва с него.');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  async function show() {
    setBusy(true); setError(null);
    try {
      setPreview(await api.post('/api/mail/debt-letter/preview', form));
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  return (
    <>
      <div className="page-head">
        <h1>Мейл към длъжници</h1>
        <div className="spacer" />
        <button disabled={busy} onClick={show}>Виж как изглежда</button>
        {canWrite && (
          <button className="primary" disabled={busy || !changed} onClick={save}>
            Запиши
          </button>
        )}
      </div>

      <p className="hint" style={{ marginTop: 0 }}>
        Таблицата с фактурите, сумите и подписът се сглобяват от програмата и
        влизат сами на мястото си. Тук се пишат думите около тях.
        {!canWrite && ' Само за преглед — текстът се сменя от администратор.'}
      </p>

      {error && <div className="error">{error}</div>}
      {done && <div className="note">{done}</div>}

      <div className="card" style={{ maxWidth: 760, marginBottom: 14 }}>
        <div className="card-head">Какво може да се впише в текста</div>
        <div className="card-body">
          <table className="inner">
            <tbody>
              {data.vars.map(([name, what]) => (
                <tr key={name}>
                  <td style={{ width: 190 }}><code>{name}</code></td>
                  <td className="muted">{what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {POLETA.map((p) => (
        <div className="card" key={p.key} style={{ maxWidth: 760, marginBottom: 10 }}>
          <div className="card-head">
            {p.label}
            {canWrite && custom(p.key) && (
              <>
                <span className="spacer" />
                <button className="link" onClick={() => set(p.key, data.defaults[p.key])}>
                  върни подразбирания
                </button>
              </>
            )}
          </div>
          <div className="card-body">
            {p.rows ? (
              <textarea
                rows={p.rows}
                style={{ width: '100%' }}
                value={form[p.key] ?? ''}
                readOnly={!canWrite}
                onChange={(e) => set(p.key, e.target.value)}
              />
            ) : (
              <input
                style={{ width: '100%' }}
                value={form[p.key] ?? ''}
                readOnly={!canWrite}
                onChange={(e) => set(p.key, e.target.value)}
              />
            )}
            {p.hint && <span className="hint">{p.hint}</span>}
          </div>
        </div>
      ))}

      {preview && (
        <div className="card" style={{ maxWidth: 760, marginTop: 14 }}>
          <div className="card-head">
            Как изглежда
            <span className="spacer" />
            <button className="link" onClick={() => setPreview(null)}>затвори</button>
          </div>
          <div className="card-body">
            <p className="muted" style={{ marginTop: 0 }}>
              По измислен длъжник: три фактури, едната още непадежирала.
            </p>
            <div style={{ marginBottom: 10 }}>
              <b>{preview.subject}</b>
            </div>
            <div
              style={{ border: '1px solid var(--line)', borderRadius: 6, padding: 12 }}
              dangerouslySetInnerHTML={{ __html: preview.html }}
            />
          </div>
        </div>
      )}
    </>
  );
}
